import { useMemo } from 'react';
import { orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  HISTORY_SANDBOX_STATUSES,
  codeSandboxesCollection,
  normalizeCodeSandboxPatch,
} from '../lib/codeSandboxReview';
import { mapQueryDocs, useFirestoreQuery } from './useFirestoreQuery';

/**
 * @param {string | null | undefined} companyId
 * @returns {string | null}
 */
function resolveCompanyId(companyId) {
  if (typeof companyId !== 'string') return null;
  const trimmed = companyId.trim();
  return trimmed || null;
}

/**
 * Live list of processed AI code patches for a company.
 * @param {string | null | undefined} companyId
 */
export function useHistoryCodeSandboxes(companyId) {
  const resolvedCompanyId = resolveCompanyId(companyId);

  const sandboxesQuery = useMemo(() => {
    if (!resolvedCompanyId) return null;
    return query(
      codeSandboxesCollection(db, resolvedCompanyId),
      where('status', 'in', HISTORY_SANDBOX_STATUSES),
      orderBy('createdAt', 'desc'),
    );
  }, [resolvedCompanyId]);

  const mapDocs = useMemo(
    () => (snap) => mapQueryDocs(snap)
      .map((row) => normalizeCodeSandboxPatch(row))
      .filter(Boolean),
    [],
  );

  const { data, loading, error } = useFirestoreQuery(sandboxesQuery, {
    enabled: Boolean(resolvedCompanyId),
    mapDocs,
  });

  return {
    patches: Array.isArray(data) ? data : [],
    loading: Boolean(resolvedCompanyId) && loading,
    error,
  };
}
